import { Maybe, UploadFile, useWebPageConfigByPathHostQuery } from '@ts/operations'
import { isString } from 'lodash'
import Head from 'next/head'
import React, { createContext, useContext } from 'react'

import { ThemeOptions } from '../theming/Theme'
import useDeepMemo from '../useDeepMemo'

type MetaData = Partial<{
  facebookId: Maybe<string>
  facebookPublisher: Maybe<string>
  image: Maybe<Partial<UploadFile>>
  siteDescription: string
  siteTitle: string
  twitterCreator: Maybe<string>
  twitterId: Maybe<string>
}>

export type SiteConfig = {
  baseGoogleAnalyticsTrackingId?: string
  googleAnalyticsLinkerDomains?: string[]
  googleAnalyticsTrackingId?: string
  googleFontsUrl?: string
  googleOptimizeId?: string
  googleTagManagerId?: string
  headerBranding?: Maybe<Partial<UploadFile>>
  logo?: Maybe<Partial<UploadFile>>
  matomoSiteId?: string
  matomoTagManagerContainerId?: string
  matomoUrl?: string
  metaData?: MetaData
  segmentId?: string
  siteTitle?: string
  termsAndConditionsUrl?: string
  themeOptions?: ThemeOptions
  ticketLogo?: Maybe<Partial<UploadFile>>
  typekitFontsUrl?: string
}

export const SiteConfigContext = createContext<SiteConfig>({})

export const getWebPageConfigByPathHostVariables = (path = '/', eventSlug?: string) => ({
  host: process.env.WEB_HOST as string,
  path: path.split('?')[0],
  slug: eventSlug,
})
export const SiteConfigProvider = ({
  children,
  path,
  eventSlug,
}: {
  children: React.ReactNode
  eventSlug?: string
  path?: string
}) => {
  const { data } = useWebPageConfigByPathHostQuery({
    variables: getWebPageConfigByPathHostVariables(path, eventSlug),
  })

  const baseGoogleAnalyticsTrackingId =
    data?.webPageConfigByPathHost?.config?.baseGoogleAnalyticsTrackingId || undefined
  const googleAnalyticsTrackingId =
    data?.webPageConfigByPathHost?.config?.googleAnalyticsTrackingId || undefined
  const googleTagManagerId = data?.webPageConfigByPathHost?.config?.googleTagManagerId || undefined
  const googleOptimizeId = data?.webPageConfigByPathHost?.config?.googleOptimizeId || undefined

  const matomoSiteId = data?.webPageConfigByPathHost?.config?.matomoSiteId || undefined
  const matomoTagManagerContainerId =
    data?.webPageConfigByPathHost?.config?.matomoTagManagerContainerId || undefined
  const matomoUrl = data?.webPageConfigByPathHost?.config?.matomoUrl || undefined
  const segmentId = data?.webPageConfigByPathHost?.config?.segmentId || undefined

  const tempMetaData = data?.webPageConfigByPathHost?.config?.metaData ?? undefined
  const metaData = useDeepMemo(() => tempMetaData, [tempMetaData])

  const tempLinkerDomains = data?.webPageConfigByPathHost?.config?.googleAnalyticsLinkerDomains
  const linkerDomains = useDeepMemo(
    () => tempLinkerDomains?.map(({ domain }) => domain).filter(isString) ?? undefined,
    [tempLinkerDomains]
  )
  const tempThemeOptions = data?.webPageConfigByPathHost?.branding?.theme
  const themeOptions = useDeepMemo(() => (tempThemeOptions as unknown) as ThemeOptions, [
    tempThemeOptions,
  ])

  const googleFontsUrl = data?.webPageConfigByPathHost?.branding?.googleFontsUrl || undefined
  const typekitFontsUrl = data?.webPageConfigByPathHost?.branding?.typekitFontsUrl || undefined

  const tempLogo = data?.webPageConfigByPathHost?.branding?.logo
  const logo = useDeepMemo(() => tempLogo as SiteConfig['logo'], [tempLogo])

  const tempHeaderBranding = data?.webPageConfigByPathHost?.branding?.headerBranding
  const headerBranding = useDeepMemo(() => tempHeaderBranding, [tempHeaderBranding])

  const favicon = data?.webPageConfigByPathHost?.branding?.favicon?.absoluteUrl
  const siteTitle = data?.webPageConfigByPathHost?.site?.title ?? undefined

  const tempTicketLogo = data?.webPageConfigByPathHost?.branding?.ticketLogo
  const ticketLogo = useDeepMemo(() => tempTicketLogo as SiteConfig['ticketLogo'], [tempTicketLogo])

  const termsAndConditionsUrl =
    data?.webPageConfigByPathHost?.event?.termsAndConditionsUrl || undefined

  if (!data) return null

  return (
    <SiteConfigContext.Provider
      value={{
        baseGoogleAnalyticsTrackingId,
        googleAnalyticsLinkerDomains: linkerDomains,
        googleAnalyticsTrackingId,
        googleFontsUrl,
        googleTagManagerId,
        headerBranding,
        logo,
        matomoSiteId,
        matomoTagManagerContainerId,
        matomoUrl,
        metaData,
        segmentId,
        siteTitle,
        termsAndConditionsUrl,
        themeOptions,
        ticketLogo,
        typekitFontsUrl,
      }}
    >
      <Head>
        {favicon ? <link href={favicon} rel="icon" /> : null}
        {googleOptimizeId && (
          <script src={`https://www.googleoptimize.com/optimize.js?id=${googleOptimizeId}`} />
        )}
      </Head>
      {children}
    </SiteConfigContext.Provider>
  )
}

export const useSiteConfig = () => useContext(SiteConfigContext)
